import { cloneDeep } from "lodash";
import moment from "moment";

import { PredefinedAppointment } from "@/Appointments/Api/Dto/Responses/PredefinedAppointment";
import { PredefinedRuleSet } from "@/Appointments/Api/Dto/Responses/PredefinedRuleSet";
import { SchedulerHelper } from "@/Appointments/Helpers/SchedulerHelper";
import { ServiceDefinitionWithItsRelatedCombos } from "@/Appointments/Models/Scheduling/ComboServiceModel";
import { PredefinedRuleSetModel } from "@/Appointments/Models/Scheduling/PredefinedRuleSetModel";
import { TemplateInfo } from "@/Appointments/Models/Scheduling/TemplateInfo";
import { TemplateLight } from "@/Appointments/Models/Scheduling/TemplateLight";
import { CalendarSlotV2, WorkDayV2 } from "@/Appointments/Models/Scheduling/WorkDayV2";
import { IOpenScheduleTabModel } from "@/Appointments/Models/UI/Scheduling/IOpenScheduleTabModel";
import { Appointment } from "@/Common/Api/Appointment/Dto/Appointment";
import { Client } from "@/Common/Api/Client";
import { ButtonControl } from "@/Common/Components/Buttons/ButtonControl";
import ButtonGroup from "@/Common/Components/Buttons/ButtonGroup";
import { RefreshButton } from "@/Common/Components/Buttons/RefreshButton";
import { Combobox, ComboBoxDropDownItem } from "@/Common/Components/Inputs/Combobox";
import { DateSelector } from "@/Common/Components/Inputs/DateSelector";
import { TextboxControl } from "@/Common/Components/Inputs/TextboxControl";
import { TextboxControlWithDatePicker } from "@/Common/Components/Inputs/TextboxControlWithDatePicker";
import { TimeSelector } from "@/Common/Components/Inputs/TimeSelector";
import { Popup } from "@/Common/Components/Modals/Popup";
import { DateTime } from "@/Common/Helpers/DateTime";
import { EnumHelper } from "@/Common/Helpers/EnumHelper";
import { GuidHelper } from "@/Common/Helpers/GuidHelper";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { DateTimeWithPrecision } from "@/Common/Models/DateTimeWithPrecision";
import { LifeStageDatePrecisionEnum } from "@/Common/Models/LifeStageDatePrecisionEnum";
import { LifeStageDateStatusEnum } from "@/Common/Models/LifeStageDateStatusEnum";
import { Person } from "@/Common/Models/Person/Person";
import { SearchableItemModelT } from "@/Common/Models/SearchableItem";
import { ServiceDefinitionModelEmployee } from "@/Common/Models/Services/ServiceDefinitionModel";
import { ServiceModel } from "@/Common/Models/Services/ServiceModel";
import { SitesModel } from "@/Common/Models/Sites/SitesModel";
import { SiteUIModel } from "@/Common/Models/Sites/SiteUIModel";
import { CalendarSlotType } from "@/Common/Models/UI/Enums";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { AppointmentSchedulerV2 } from "./AppointmentSchedulerV2";
import { EditServiceOptions } from "./EditServiceOptions";
import { RulesetEditComponent, RulesetEditComponentDTO } from "./RulesetEditComponent";
import { SearchComboServiceComponent } from "./SearchComboServiceComponent";

import styles from "./OpenScheduleTab.module.scss";

import img_loading from "@/Globals/_images/loading.gif";

interface IOpenScheduleTabViewProps {
    Model: IOpenScheduleTabModel;
}

class OpenScheduleTabViewState {
    public EmployeesFiltered: Immutable.List<SearchableItemModelT<Person>>;
    public ServicesFiltered: Immutable.List<SearchableItemModelT<ServiceModel>>;

    // scheduler setup
    public SelectedEmployee: Person;
    public SelectedDay: Date;
    public SelectedDaytext;
    public SelectedSiteId;
    public OpenSheduleType: OpenSheduleType;
    public ServiceDurationMinutes: number;
    public HourStart: Date;
    public HourEnd: Date;
    public PreferedDisplayResolution: number;
    public WorkDays: Immutable.List<WorkDayV2>;
    public IsLoading: boolean;

    // service mode
    public SelectedService: ServiceModel;
    public AddServicePopupOpen: boolean;

    // ruleset mode
    public SelectedComboServiceList: Immutable.List<ServiceDefinitionWithItsRelatedCombos>;
    public SelectedRuleSetForEdit: PredefinedRuleSetModel;
    public MinErrorPopupOpen: boolean;
    public ruleset_EditedRulesetDTO: RulesetEditComponentDTO;

    // actions
    public AddPopupOpen: boolean;
    public DeletePopupOpen: boolean;
    public selectedRecurrenceType: string;
    public RecurrenceEndDate: Date;
    public RecurrenceEndDateDelete: Date;
    public hasDoubleBookingCollision: boolean;
    public ReservedDate: Date[];
    public selectedRecurrenceTypeDelete: string;
    public HourStartDelete: Date;
    public HourEndDelete: Date;

    public SelectAllSiteForDelete: boolean;

    public SitesListForDelete: Immutable.List<SiteUIModel>;

    public TemplateList: TemplateLight[];
    public SelectedTemplateId: string;
    public templateHasRuleSets: boolean;
    public templateBookingHubVisibility: BookingHubVisibility;

    public TemplateWorkDays: Immutable.List<WorkDayV2>;

    public selectedDeleteType: string;

    public waitWhileAddingPopup: boolean;
}

enum OpenSheduleType {
    Service,
    RuleSet,
    Template
}

enum BookingHubVisibility {
    None,
    All,
    Partial
}

class OpenScheduleTabView extends React.Component<IOpenScheduleTabViewProps, OpenScheduleTabViewState> {
    private timeoutReferenceRefreshCalendar: number;
    private employees: Immutable.List<SearchableItemModelT<Person>> = Immutable.List([]);
    private LastCalendarRequest: string;

    private recurrenceType = [
        new ComboBoxDropDownItem("0", cacheStore.GetString("UniqueLabel"), 0, null),
        new ComboBoxDropDownItem("1", cacheStore.GetString("DailyLabel"), 1, null),
        new ComboBoxDropDownItem("2", cacheStore.GetString("WeekdaysLabel"), 2, null),
        new ComboBoxDropDownItem("3", cacheStore.GetString("WeeklyLabel"), 3, null),
        new ComboBoxDropDownItem("4", cacheStore.GetString("MonthlyLabel"), 4, null),
        new ComboBoxDropDownItem("5", cacheStore.GetString("AnnuallyLabel"), 5, null)
    ];

    private deleteTypeList = [
        new ComboBoxDropDownItem("0", cacheStore.GetString("AllLabel"), 0, null),
        new ComboBoxDropDownItem("1", cacheStore.GetString("RuleSetLabel"), 1, null),
        new ComboBoxDropDownItem("2", cacheStore.GetString("ServiceLabel"), 2, null)
    ];

    private getInitialState(): OpenScheduleTabViewState {
        let state = new OpenScheduleTabViewState();
        state.EmployeesFiltered = this.employees;
        state.ServicesFiltered = [].toImmutable();
        state.OpenSheduleType = OpenSheduleType.Template;
        state.SelectedSiteId = sessionStore.getCurrentEmployee().getActiveSite().Model.Id;
        state.HourStart = new Date(DateTime.nowDeprecated().getFullYear(), DateTime.nowDeprecated().getMonth(), DateTime.nowDeprecated().getDate(), 8);
        state.HourEnd = new Date(DateTime.nowDeprecated().getFullYear(), DateTime.nowDeprecated().getMonth(), DateTime.nowDeprecated().getDate(), 12);
        state.WorkDays = [].toImmutable();
        state.IsLoading = false;
        state.SelectedDay = DateTime.nowDeprecated();
        state.SelectedDaytext = DateTime.nowMoment().format("YYYY-MM-DD");
        state.SelectedRuleSetForEdit = null;
        state.PreferedDisplayResolution = 15;
        state.AddPopupOpen = false;
        state.DeletePopupOpen = false;
        state.RecurrenceEndDate = null;
        state.RecurrenceEndDateDelete = null;
        state.hasDoubleBookingCollision = false;
        state.ReservedDate = null;
        state.HourStartDelete = null;
        state.HourEndDelete = null;
        state.AddServicePopupOpen = false;
        state.SelectAllSiteForDelete = true;
        state.SelectedComboServiceList = Immutable.fromJS([]);
        state.ruleset_EditedRulesetDTO = null;

        state.selectedRecurrenceType = this.recurrenceType[0].Code;
        state.selectedRecurrenceTypeDelete = this.recurrenceType[0].Code;

        let sitesList = cacheStore.SiteCache.map(item => {
            let site = SiteUIModel.FromCompositeObject(item);
            site.Checked = true;

            return site;
        });

        state.SitesListForDelete = sitesList.toImmutable();

        state.TemplateList = [];
        state.SelectedTemplateId = "";
        state.TemplateWorkDays = [].toImmutable();
        state.templateHasRuleSets = false;

        state.selectedDeleteType = "0";

        state.waitWhileAddingPopup = false;

        return state;
    }

    constructor(props: IOpenScheduleTabViewProps) {
        super(props);
        this.employees = cacheStore.Clinicians.map(x => new SearchableItemModelT<Person>(x.NameWithPrefix, x.ContactId, x)).toImmutable();

        if (schedulingStore.currentState == null)
            this.state = this.getInitialState();
        else
            this.state = schedulingStore.currentState;
    }

    public componentDidMount(): void {
        Client.Post(ServerOperations.Server.Myle_AdministrationGetEmployees, {}, response => {
            cacheStore.RefreshEmployeesRaw(response);
            this.employees = cacheStore.EmployeeCache.map(x => {
                let person = Person.FromEmployee(x);
                return new SearchableItemModelT<Person>(person.NameWithPrefix, person.ContactId, person);
            }).toImmutable();

            this.mutateState((s: OpenScheduleTabViewState) => {
                s.EmployeesFiltered = this.employees;
            });
        }, () => { });

        window.setTimeout(() => {
            $(window).trigger("resize");
            $("#new_sched_starttime").parent().css({ width: "120px" });
            $("#new_sched_endtime").parent().css({ width: "120px" });
        }, 150);
    }

    public componentDidUpdate(prevProps: Readonly<IOpenScheduleTabViewProps>): void {
        if (this.props !== prevProps) {
            this.mutateState(s => {
                if (this.props.Model.get("SelectedRuleset") != this.state.SelectedRuleSetForEdit) {
                    s.SelectedRuleSetForEdit = this.props.Model.get("SelectedRuleset");

                    // keep old selected info if ruleset is cleared, else replace with new ruleset values
                    if (s.SelectedRuleSetForEdit != null) {
                        s.OpenSheduleType = OpenSheduleType.RuleSet;
                        s.SelectedSiteId = s.SelectedRuleSetForEdit.Model.SiteId;
                        s.HourStart = moment(s.SelectedRuleSetForEdit.Model.StartDate).toDate();
                        s.HourEnd = moment(s.SelectedRuleSetForEdit.Model.EndDate).toDate();

                        // now convert ruleset object to selectedcombo compatible with rulesetEditControl
                        let comboServiceList = s.SelectedRuleSetForEdit.ToComboServiceList();
                        comboServiceList.sort((a, b) => {
                            if (a.FullName > b.FullName)
                                return 1;
                            if (a.FullName < b.FullName)
                                return -1;
                            return 0;
                        });
                        s.SelectedComboServiceList = Immutable.List(comboServiceList);
                    }
                }
            });
        }

        $(window).trigger("resize");
        $("#new_sched_starttime").parent().css({ width: "120px" });
        $("#new_sched_endtime").parent().css({ width: "120px" });
    }

    public componentWillUnmount(): void {
        schedulingStore.currentState = this.state;
    }

    public render(): JSX.Element {
        return (
            <div className="workspace">
                <div className="fxheight hide-overflow">
                    {this.renderHeaderRow()}

                    <div className="fixed">
                        <div className="flex-container" style={{ justifyContent: "space-between", width: "100%" }}>
                            {this.renderLeft()}

                            {this.renderRight()}

                        </div>
                    </div>
                </div>

                <div data-ctrlref className="ws-footer table-pager-footer" style={{ height: "0px", padding: "0px", margin: "0px" }} />
            </div>
        );
    }

    private renderLeft(): JSX.Element {
        return (
            <div className="open-schedule-leftside">

                {this.renderLeftAddPopup()}
                {this.renderdoubleBookingPopupError()}
                {this.renderLeftWaitWhileAddingPopup()}
                {this.renderLeftDeletePopup()}
                {this.renderLeftAddServicePopup()}
                {this.renderLeftMinErrorPopup()}

                <div className="flex-container">
                    <div className="pad-right-20">
                        {this.renderLeftIntervenorAndTypeSelection()}
                        {this.renderLeftSiteSelection()}
                    </div>
                    <div>
                        {this.renderLeftActionButtons()}
                    </div>
                </div>

                {this.state.OpenSheduleType === OpenSheduleType.Service &&
                    this.renderLeftOpenService()
                }

                {(this.state.OpenSheduleType === OpenSheduleType.RuleSet) &&
                    this.renderLeftOpenRuleset()
                }

                {this.state.OpenSheduleType === OpenSheduleType.Template &&
                    this.renderLeftOpenTemplate()
                }
            </div>
        );
    }

    private renderLeftAddPopup(): JSX.Element {
        if (this.state.AddPopupOpen != true) return null;

        const closeAddPopup = (): void => {
            this.mutateState(s => s.AddPopupOpen = false);
            sessionStore.hideGlobalOverlay();
        };

        return (
            <Popup cssClass="double-booking" draggable={true} title={cacheStore.GetString("AddAvailabilitiesLabel")} onCloseDelegate={() => closeAddPopup()} dataCy="schedule-recurrence-popup">
                <div className="p-body">
                    <div className="row">
                        <div className="col-lg-2 col-md-2 col-sm-2" style={{ paddingTop: "10px" }}>
                            <span>{cacheStore.GetString("TypeLabel")}</span>
                        </div>
                        <div className="col-lg-10 col-md-10 col-sm-10">
                            <Combobox
                                HasEmptyItem={false}
                                id="_MyComboBox"
                                items={this.recurrenceType.toImmutable()}
                                selectedCode={this.state.selectedRecurrenceType}
                                onChange={(recurrenceCode: string) => this.mutateState(s => s.selectedRecurrenceType = recurrenceCode)}
                                dataCy="schedule-recurrenceType"
                            />
                        </div>
                    </div>
                    {this.state.selectedRecurrenceType !== "0" && (
                        <div className="row" style={{ paddingTop: "5px" }}>

                            <div className="col-lg-2 col-md-2 col-sm-2" style={{ paddingTop: "10px" }}>
                                <span>{cacheStore.GetString("EndDateDLabel")}</span>
                            </div>
                            <div className="col-lg-10 col-md-10 col-sm-10">
                                <DateSelector
                                    id="dttt01_BillDate"
                                    date={this.state.RecurrenceEndDate}
                                    onChangeCallback={date => this.mutateState(s => s.RecurrenceEndDate = date)}
                                    dateFormat="yyyy-MM-dd"
                                    disabled={false}
                                    toDayEnabled={true}
                                    dataCy="schedule-recurrenceEndDate"
                                />
                            </div>
                        </div>
                    )}

                </div>
                <div className="p-footer flex-display flex-end">
                    <ButtonGroup>
                        <ButtonControl
                            variant="neutral"
                            onClick={() => closeAddPopup()}
                            label={cacheStore.GetString("CancelLabel")}
                        />
                        {(this.state.OpenSheduleType === OpenSheduleType.RuleSet) && (
                            <ButtonControl
                                variant="brand"
                                onClick={this.checkRuleSetAvailability.bind(this)}
                                label={cacheStore.GetString("AddLabel")}
                            />
                        )}
                        {this.state.OpenSheduleType === OpenSheduleType.Service && (
                            <ButtonControl
                                variant="brand"
                                onClick={this.onAddAffectation.bind(this)}
                                label={cacheStore.GetString("AddLabel")}
                            />
                        )}
                        {this.state.OpenSheduleType === OpenSheduleType.Template && (
                            <ButtonControl
                                variant="brand"
                                onClick={this.onAddTemplateClick.bind(this)}
                                label={cacheStore.GetString("AddLabel")}
                            />
                        )}
                    </ButtonGroup>
                </div>
            </Popup>
        );
    }

    private renderLeftDeletePopup(): JSX.Element {
        if (this.state.DeletePopupOpen != true) return null;

        const closePopup = (): void => {
            this.mutateState(s => s.DeletePopupOpen = false);
            sessionStore.hideGlobalOverlay();
        };

        return (
            <Popup cssClass="double-booking" draggable={true} onCloseDelegate={() => closePopup()} title={cacheStore.GetString("RemoveAvailabilitiesLabel")}>
                <div className="p-body">
                    <div className="row">
                        <div className="col-lg-3 col-md-3 col-sm-3" style={{ paddingTop: "10px" }}>
                            <div className="text-right">
                                <span>{cacheStore.GetString("TypeLabel")}</span>
                            </div>
                        </div>
                        <div className="col-lg-9 col-md-9 col-sm-9">
                            <Combobox
                                HasEmptyItem={false}
                                doInitialChangeOp={false}
                                id="_MyComboDeleteBox"
                                items={this.recurrenceType.toImmutable()}
                                selectedCode={this.state.selectedRecurrenceTypeDelete}
                                onChange={(recurrenceCode: string) => this.mutateState(s => s.selectedRecurrenceTypeDelete = recurrenceCode)}
                                dataCy="schedule-recurrenceTypeDelete"
                            />
                        </div>
                    </div>
                    <div className="row" style={{ paddingTop: "5px" }}>
                        <div className="col-lg-3 col-md-3 col-sm-3" style={{ paddingTop: "10px" }}>
                            <div className="text-right">
                                <span>{cacheStore.GetString("DeleteLabel")}</span>
                            </div>
                        </div>
                        <div className="col-lg-9 col-md-9 col-sm-9">
                            <Combobox
                                HasEmptyItem={false}
                                doInitialChangeOp={false}
                                id="_MyComboDeleteTypeBox"
                                items={this.deleteTypeList.toImmutable()}
                                selectedCode={this.state.selectedDeleteType}
                                onChange={(deletedCode: string) => this.mutateState(s => s.selectedDeleteType = deletedCode)}
                                dataCy="schedule-deleteType"
                            />
                        </div>
                    </div>
                    <div className="row" style={{ paddingTop: "5px" }}>
                        <div className="col-lg-3 col-md-3 col-sm-3" style={{ paddingTop: "10px" }}>
                            <div className="text-right">
                                <span>{cacheStore.GetString("EndDateDLabel")}</span>
                            </div>
                        </div>
                        <div className="col-lg-9 col-md-9 col-sm-9">
                            <DateSelector
                                id="dttt02_BillDate"
                                date={this.state.RecurrenceEndDateDelete}
                                onChangeCallback={date => this.mutateState(s => s.RecurrenceEndDateDelete = date)}
                                dateFormat="yyyy-MM-dd"
                                disabled={false}
                                toDayEnabled={true}
                                dataCy="schedule-recurrenceEndDateDelete"
                            />
                        </div>
                    </div>
                    <div className="row" style={{ paddingTop: "5px" }}>
                        <div className="col-lg-3 col-md-3 col-sm-3" style={{ paddingTop: "10px" }}>
                            <div className="text-right">
                                <span>{cacheStore.GetString("FromLabel")}</span>
                            </div>
                        </div>
                        <div className="col-lg-9 col-md-9 col-sm-9">
                            <TimeSelector
                                interval={5}
                                id="new_sched_starttime_delete"
                                cssClass="rounded"
                                date={this.state.HourStartDelete}
                                onChangeCallback={_date => this.mutateState(s => s.HourStartDelete = _date)}
                                disabled={false}
                                isRequired
                                dataCy="schedule-hourStartDelete"
                            />
                        </div>
                    </div>
                    <div className="row" style={{ paddingTop: "5px" }}>
                        <div className="col-lg-3 col-md-3 col-sm-3" style={{ paddingTop: "10px" }}>
                            <div className="text-right">
                                <span>{cacheStore.GetString("ToLabel")}</span>
                            </div>
                        </div>
                        <div className="col-lg-9 col-md-9 col-sm-9">
                            <TimeSelector
                                interval={5}
                                id="new_sched_endtime_delete"
                                cssClass="rounded"
                                date={this.state.HourEndDelete}
                                onChangeCallback={_date => this.mutateState(s => s.HourEndDelete = _date)}
                                disabled={false}
                                isRequired
                                dataCy="schedule-hourEndDelete"
                            />
                        </div>
                    </div>

                    {cacheStore.SiteCache.length > 1 && (
                        <div className="row" style={{ paddingTop: "5px" }}>
                            <div className="col-lg-3 col-md-3 col-sm-3" style={{ paddingTop: "10px" }}>
                                <div className="text-right">
                                    <span>{cacheStore.GetString("SiteDLabel")}</span>
                                </div>
                            </div>
                            <div className="col-lg-9 col-md-9 col-sm-9" style={{ "height": "200px", "overflow": "auto" }}>
                                <div className="checkbox" key="uniqueIdForClickDiv">
                                    <label onClick={() => this.onSelectAllSiteForDeleteClick()}>
                                        <input type="checkbox" id="uniqueIdForClickDiv" checked={this.state.SelectAllSiteForDelete} />
                                        <div>
                                            <label>{cacheStore.GetString("SelectAllLabel")}</label>
                                        </div>
                                    </label>
                                </div>
                                <div className="checkbox">
                                    {this.state.SitesListForDelete.toArray().map((item: SiteUIModel, idx: number) => (
                                        <div key={idx + "_se"} className="checkbox">
                                            <label
                                                onClick={() =>
                                                    this.mutateState(s => {
                                                        s.SitesListForDelete = s.SitesListForDelete.toArray().map(c => {
                                                            if (c.Id == item.Id)
                                                                c.Checked = !c.Checked;

                                                            return c;
                                                        }).toImmutable();
                                                    })
                                                }
                                            >
                                                <input type="checkbox" id={item.Id} checked={item.Checked} />
                                                <div>
                                                    <label>{item.Name}</label>
                                                </div>
                                            </label>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                </div>
                <div className="p-footer flex-display flex-end">
                    <ButtonGroup>
                        <ButtonControl
                            variant="neutral"
                            onClick={() => closePopup()}
                            label={cacheStore.GetString("CancelLabel")}
                        />
                        <ButtonControl
                            variant="brand"
                            onClick={this.onContinueDeleteClick.bind(this)}
                            label={cacheStore.GetString("ContinueLabel")}
                            dataCy="schedule-continueDelete"
                        />
                    </ButtonGroup>
                </div>
            </Popup>
        );
    }

    private renderdoubleBookingPopupError(): JSX.Element {
        let dateListRender = null;
        if (this.state.ReservedDate) {
            dateListRender = this.state.ReservedDate.map((reserved: Date, idx: number) => (
                <div key={"reservedDate_" + idx}>
                    <label>{moment(reserved).format("YYYY/MM/DD") + " " + cacheStore.GetString("LowerCaseAtLabel") + " " + moment(reserved).format("hh:mm:ss")}</label>
                </div>
            ));
        }

        if (this.state.hasDoubleBookingCollision == false)
            return null;

        const closePopup = (): void => {
            this.mutateState(s => s.hasDoubleBookingCollision = false);
            sessionStore.hideGlobalOverlay();
        };

        return (
            <Popup cssClass="double-booking" draggable={true} onCloseDelegate={() => closePopup()} title={cacheStore.GetString("WarningLabel")}>
                <div className="p-body">
                    <strong>{cacheStore.GetString("ErrorMessage1AddAvailabilityLabel")}</strong>
                    {dateListRender}
                    <strong>{cacheStore.GetString("ErrorMessage2AddAvailabilityLabel")}</strong>
                </div>
                <div className="p-footer flex-display flex-end">
                    <ButtonGroup>
                        <ButtonControl
                            variant="neutral"
                            onClick={() => closePopup()}
                            label={cacheStore.GetString("CancelLabel")}
                        />
                        <ButtonControl
                            variant="brand"
                            onClick={this.onAddAffectation.bind(this)}
                            label={cacheStore.GetString("ContinueLabel")}
                        />
                    </ButtonGroup>
                </div>
            </Popup>
        );
    }

    private renderLeftWaitWhileAddingPopup(): JSX.Element {
        if (this.state.waitWhileAddingPopup != true)
            return null;

        return (
            <Popup cssClass="double-booking">
                <div className="p-body">
                    <div>
                        <strong>{cacheStore.GetString("RequestIsBeingProceedLabel")}</strong>
                        <div>
                            <img src={img_loading} alt="loading" />
                        </div>
                    </div>
                </div>
            </Popup>
        );
    }

    private renderLeftAddServicePopup(): JSX.Element {
        if (this.state.AddServicePopupOpen != true)
            return null;

        let selectedComboService = this.state.ruleset_EditedRulesetDTO ? this.state.ruleset_EditedRulesetDTO.selectedComboServiceList : [].toImmutable();

        return (
            <Popup cssClass="double-booking" draggable={true} onCloseDelegate={() => this.showAddServicePopup(false)} title={cacheStore.GetString("AddServicesLabel")} dataCy="schedule-addServices-popup">
                <div className="p-body">
                    <SearchComboServiceComponent
                        selectedComboServiceList={selectedComboService}
                        selectedEmployeeContactId={this.state.SelectedEmployee.ContactId}
                        selectedSiteId={this.state.SelectedSiteId}
                        comboServiceListSelectionChangeOperation={LocalOperations.OpenScheduleTab.ChangeComboServiceSelection}
                        isFromOpenSchedule={true}
                    />
                </div>

                <div className="p-footer flex-display flex-end">
                    <ButtonGroup>
                        <ButtonControl
                            variant="neutral"
                            onClick={() => this.showAddServicePopup(false)}
                            label={cacheStore.GetString("CancelLabel")}
                        />
                        <ButtonControl
                            variant="brand"
                            onClick={this.onAddSelectedComboService.bind(this)}
                            label={cacheStore.GetString("AddLabel")}
                        />
                    </ButtonGroup>
                </div>
            </Popup>
        );
    }

    private renderLeftMinErrorPopup(): JSX.Element {
        if (this.state.MinErrorPopupOpen != true)
            return null;

        const closeMinError = (): void => {
            this.mutateState(s => s.MinErrorPopupOpen = false);
            sessionStore.hideGlobalOverlay();
        };

        return (
            <Popup cssClass="double-booking" draggable={true} onCloseDelegate={() => closeMinError()} title={cacheStore.GetString("WarningLabel")}>
                <div className="p-body">
                    <label>{cacheStore.GetString("MinRuleSetErrorLabel")}</label>
                </div>
                <div className="p-footer">
                    <div className="text-right">
                        <ButtonControl
                            variant="neutral"
                            onClick={() => closeMinError()}
                            label={cacheStore.GetString("ContinueLabel")}
                        />
                    </div>
                </div>
            </Popup>
        );
    }

    private renderLeftIntervenorAndTypeSelection(): JSX.Element {
        const typeList: ComboBoxDropDownItem[] = [
            new ComboBoxDropDownItem(EnumHelper.getName(OpenSheduleType, OpenSheduleType.Service), cacheStore.GetString("ServiceLabel"), 0, null),
            new ComboBoxDropDownItem(EnumHelper.getName(OpenSheduleType, OpenSheduleType.Template), cacheStore.GetString("GabaritLabel"), 2, null),
            new ComboBoxDropDownItem(EnumHelper.getName(OpenSheduleType, OpenSheduleType.RuleSet), cacheStore.GetString("RuleSetLabel"), 1, null)
        ];

        typeList.sortByProperty(p => p.Position);

        return (
            <div>
                <div className="open-schedule-row">
                    <div className="open-schedule-c1">
                        <div className="open-schedule-divlabel">
                            <label className="open-schedule-label">{cacheStore.GetString("StaffLabel")}
                            </label>
                        </div>
                    </div>
                    <div className="open-schedule-c2">
                        <div className="open-schedule-divlabel" style={{ width: "425px" }}>
                            {this.state.SelectedEmployee == null && (
                                <TextboxControl
                                    value=""
                                    autoCompleteItems={this.state.EmployeesFiltered}
                                    onAutoCompleteSelectCallBack={(code, value: Person) => {
                                        this.mutateState(
                                            _state => {
                                                _state.SelectedEmployee = value;
                                                _state.ServicesFiltered = value.EmployeeModel.ServicesOffered
                                                    .filter(x => !x.ServiceDefinitionModel.IsAdHoc && x.ServiceDefinitionModel.Model.SiteId === _state.SelectedSiteId)
                                                    .map(x => new SearchableItemModelT<ServiceModel>(
                                                        x.ServiceDefinitionModel.Name + " - ( " + x.ServiceDefinitionModel.GetDefaultDurationMinutes() + " min. ) - " + cacheStore.GetLocalizedValueByLookupId(x.ServiceDefinitionModel.Model.SpecializationLookupId),
                                                        x.ServiceID,
                                                        x
                                                    ))
                                                    .toImmutable();
                                                _state.WorkDays = [].toImmutable();
                                            },
                                            () => {
                                                this.throttledRefreshCalendar();
                                                if (this.state.OpenSheduleType === OpenSheduleType.Template) {
                                                    this.loadEmployeeTemplate();
                                                }
                                            }
                                        );
                                    }}
                                    onAutoCompleteSelectClearTextBox={false}
                                    style={{ marginRight: "10px" }}
                                    placeholder=""
                                    onMountFocus={true}
                                    dataCy="schedule-employee"
                                />
                            )}
                            {this.state.SelectedEmployee != null && (
                                <div className="open-schedule-divlabel">
                                    <div className="flex-container" style={{ justifyContent: "flex-start" }}>

                                        <div style={{ marginRight: "10px" }}>{this.state.SelectedEmployee.NameWithPrefix}</div>
                                        <div
                                            className="open-schedule-redx pointer"
                                            onClick={() => {
                                                this.mutateState(_state => {
                                                    _state.SelectedEmployee = null;
                                                    _state.ServicesFiltered = [].toImmutable();
                                                    _state.WorkDays = [].toImmutable();
                                                    _state.SelectedComboServiceList = Immutable.List([]);
                                                    _state.SelectedTemplateId = "";
                                                    _state.TemplateWorkDays = [].toImmutable();
                                                    _state.ruleset_EditedRulesetDTO = null;
                                                    _state.SelectedService = null;
                                                });
                                            }}
                                        >
                                            <span>X</span>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                    </div>
                </div>
                <div className="open-schedule-row">
                    <div className="open-schedule-c1">
                        <div className="open-schedule-divlabel">
                            <label className="open-schedule-label">{cacheStore.GetString("TypeLabel")}</label>
                        </div>
                    </div>
                    <div className="open-schedule-c2">
                        <div className="open-schedule-divlabel">
                            <div className="flex-container" style={{ width: "425px" }}>
                                <Combobox
                                    HasEmptyItem={false}
                                    doInitialChangeOp={false}
                                    id="_TypeComboBox"
                                    items={typeList.toImmutable()}
                                    selectedCode={EnumHelper.getName(OpenSheduleType, this.state.OpenSheduleType)}
                                    onChange={typeCode => this.onTypeChange(typeCode)}
                                    dataCy="schedule-type"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    private renderLeftSiteSelection(): JSX.Element {
        if (this.state.OpenSheduleType === OpenSheduleType.Template)
            return null;

        return (
            <div className="open-schedule-row">
                <div className="open-schedule-c1">
                    <div className="open-schedule-divlabel"><label className="open-schedule-label">{cacheStore.GetString("SiteDLabel")}</label></div>
                </div>
                <div className="open-schedule-c2">
                    <div className="open-schedule-divlabel" style={{ width: "425px" }}>
                        <Combobox
                            HasEmptyItem={false}
                            doInitialChangeOp={false}
                            id="_SiteComboBox"
                            items={cacheStore.SiteCache.map(x => SitesModel.ToComboboxItem(x)).toImmutable()}
                            selectedCode={this.state.SelectedSiteId}
                            onChange={selectedId => {
                                this.mutateState(_state => {
                                    _state.SelectedSiteId = selectedId;
                                    _state.SelectedComboServiceList = Immutable.List([]);
                                    _state.ServicesFiltered = _state.SelectedEmployee ? _state.SelectedEmployee.EmployeeModel.ServicesOffered.filter(x => !x.ServiceDefinitionModel.IsAdHoc && x.ServiceDefinitionModel.Model.SiteId === selectedId).map(x => new SearchableItemModelT<ServiceModel>(x.ServiceDefinitionModel.Name + " - ( " + x.ServiceDefinitionModel.GetDefaultDurationMinutes() + " min. ) - " + cacheStore.GetLocalizedValueByLookupId(x.ServiceDefinitionModel.Model.SpecializationLookupId), x.ServiceID, x)).toImmutable() : [].toImmutable();
                                    _state.SelectedService = null;
                                });
                            }}
                        />
                    </div>
                </div>
            </div>
        );
    }

    private renderLeftActionButtons(): JSX.Element {
        let startDateIsValid = true;
        let startDate = new Date(this.state.SelectedDay.getTime());
        try {
            startDate.setHours(this.state.HourStart.getHours());
            startDate.setMinutes(this.state.HourStart.getMinutes(), 0);
        }
        catch {
            startDateIsValid = false;
        }

        let endDateIsValid = true;
        let endDate = new Date(this.state.SelectedDay.getTime());
        try {
            endDate.setHours(this.state.HourEnd.getHours());
            endDate.setMinutes(this.state.HourEnd.getMinutes(), 0);
        }
        catch {
            endDateIsValid = false;
        }

        let deletionIsValidIfTypeIsService = this.state.OpenSheduleType !== OpenSheduleType.Service ? true : startDateIsValid && endDateIsValid;

        let hasMinRuleSetDuration = false;
        let duration = endDate.valueOf() - startDate.valueOf();
        if (duration >= 300000) { // for ruleset 5minutes minimum
            hasMinRuleSetDuration = true;
        }

        // disable add if we are in ruleset mode, duration is <5min and nothing has been edited
        let isAddDisabledForRuleset = false;
        if (this.state.OpenSheduleType === OpenSheduleType.RuleSet && (!this.hasSelectedComboService(this.state.SelectedComboServiceList) || !hasMinRuleSetDuration))
            isAddDisabledForRuleset = true;

        if (this.state.ruleset_EditedRulesetDTO && (!this.state.ruleset_EditedRulesetDTO.canSave || !this.hasSelectedComboService(this.state.ruleset_EditedRulesetDTO.selectedComboServiceList)))
            isAddDisabledForRuleset = true;

        return (
            <div className={styles.addDeleteButtonContainer}>
                {/** addDeleteButtonContainer css class makes it so the width of the following buttons are the same. Need to fix this once Design system has label button. */}
                {(this.state.OpenSheduleType === OpenSheduleType.RuleSet) && (
                    <ButtonControl
                        variant="brand"
                        disabled={isAddDisabledForRuleset}
                        onClick={() => this.openAddSchedulePopup(true)}
                        label={cacheStore.GetString("AddLabel") + " >>"}
                        dataCy="schedule-add"
                    />
                )}
                {this.state.OpenSheduleType === OpenSheduleType.Service && (
                    <ButtonControl
                        variant="brand"
                        disabled={this.state.SelectedService == null || this.state.ServiceDurationMinutes <= 0 || !startDateIsValid || !endDateIsValid}
                        onClick={() => this.openAddSchedulePopup()}
                        label={cacheStore.GetString("AddLabel") + " >>"}
                        dataCy="schedule-add"
                    />
                )}
                {this.state.OpenSheduleType === OpenSheduleType.Template && (
                    <ButtonControl
                        variant="brand"
                        disabled={this.state.SelectedTemplateId == ""}
                        onClick={() => this.openAddSchedulePopup()}
                        label={cacheStore.GetString("AddLabel") + " >>"}
                        dataCy="schedule-add"
                    />
                )}
                <ButtonControl
                    disabled={this.state.SelectedEmployee == null || !deletionIsValidIfTypeIsService}
                    variant="error"
                    onClick={this.onShowDeletePopup.bind(this)}
                    label={"<< " + cacheStore.GetString("DeleteLabel")}
                    dataCy="schedule-delete"
                />
            </div>
        );
    }

    private hasSelectedComboService(comboServiceList: Immutable.List<ServiceDefinitionWithItsRelatedCombos>): boolean {
        if (!comboServiceList || comboServiceList.count() === 0)
            return false;

        let comboSerivceArray = comboServiceList.toArray();

        let standAlone = comboSerivceArray.filter(s => !s.HasCombo);
        if (standAlone.length > 0)
            return true;

        let combo = comboSerivceArray.filter(s => s.HasCombo && s.RelatedServiceDefinitionContexts.length > 0);
        if (combo.length > 0)
            return true;

        return false;
    }

    private renderLeftOpenService(): JSX.Element {
        return (
            <div>
                <div className="open-schedule-row">
                    <div className="open-schedule-c1">
                        <div className="open-schedule-divlabel"><label className="open-schedule-label">{cacheStore.GetString("DateLabel")}</label></div>
                    </div>
                    <div className="open-schedule-c2">
                        <div className="open-schedule-divlabel">

                            <TextboxControlWithDatePicker
                                id="resolutionDP"
                                disabled={this.state.SelectedEmployee == null}
                                textValue={this.state.SelectedDaytext}
                                dateValue={this.state.SelectedDay}
                                width={150}
                                expectedPrecisionMinimum={LifeStageDatePrecisionEnum.Day}
                                onChange={(datePrecision: DateTimeWithPrecision) => {
                                    if (datePrecision.status == LifeStageDateStatusEnum.Valid)
                                        this.mutateState(
                                            _state => {
                                                _state.SelectedDaytext = datePrecision.text;
                                                _state.SelectedDay = datePrecision.value;
                                            },
                                            () => { this.throttledRefreshCalendar(); }
                                        );
                                }}
                                dataCy="schedule-date"
                            />
                        </div>
                    </div>
                </div>
                <div className="open-schedule-row">
                    <div className="open-schedule-c1">
                        <div className="open-schedule-divlabel"><label className="open-schedule-label">{cacheStore.GetString("FromLabel")}</label></div>
                    </div>
                    <div className="open-schedule-c2">
                        <div className="open-schedule-divlabel">
                            <div className="flex-container" style={{ justifyContent: "flex-start" }}>
                                <div style={{ width: "150px" }}>
                                    <TimeSelector
                                        interval={5}
                                        id="new_sched_starttime"
                                        cssClass="rounded"
                                        date={this.state.HourStart}
                                        width={150}
                                        onChangeCallback={(_date: Date) => {
                                            this.mutateState(_state => {
                                                _state.HourStart = _date;
                                            });
                                        }}
                                        disabled={false}
                                        isRequired
                                        dataCy="schedule-from"
                                    />
                                </div>
                                <div className="open-schedule-divlabel" style={{ width: "35px", paddingLeft: "10px" }}><label className="open-schedule-label">{cacheStore.GetString("ToLabel")}</label></div>
                                <div>
                                    <TimeSelector
                                        interval={5}
                                        id="new_sched_endtime"
                                        cssClass="rounded"
                                        date={this.state.HourEnd}
                                        width={150}
                                        onChangeCallback={(_date: Date) => {
                                            this.mutateState(_state => {
                                                _state.HourEnd = _date;
                                            });
                                        }}
                                        disabled={false}
                                        isRequired
                                        dataCy="schedule-to"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="open-schedule-row">
                    <div className="open-schedule-c1">
                        <div className="open-schedule-divlabel"><label className="open-schedule-label">{cacheStore.GetString("ServicesLabel")}</label></div>
                    </div>
                    <div className="open-schedule-c2">
                        <div className="open-schedule-divlabel" style={{ width: "425px" }}>
                            {this.state.SelectedService == null && (
                                <TextboxControl
                                    value=""
                                    autoCompleteItems={this.state.SelectedEmployee
                                        ? cacheStore
                                            .GetEmployeeByContactIdOrDefault(this.state.SelectedEmployee.ContactId)
                                            .ServicesOffered.filter(x => !x.ServiceDefinitionModel.IsAdHoc && x.ServiceDefinitionModel.Model.SiteId === this.state.SelectedSiteId)
                                            .map(x =>
                                                // Refresh services --> A refresh should not be required when a service is created to open a schedule to an employee (in Schedule Affectation)
                                                new SearchableItemModelT<ServiceModel>(x.ServiceDefinitionModel.Name + " - ( " + x.ServiceDefinitionModel.GetDefaultDurationMinutes() + " min. ) - " + cacheStore.GetLocalizedValueByLookupId(x.ServiceDefinitionModel.Model.SpecializationLookupId), x.ServiceID, x)
                                            )
                                            .toImmutable()
                                        : null
                                    }
                                    onAutoCompleteSelectCallBack={(code, value: ServiceModel) => {
                                        this.mutateState(_state => {
                                            _state.SelectedService = cloneDeep(value);
                                            _state.ServiceDurationMinutes = value.GetDefaultDurationMinutes();
                                        });
                                    }}
                                    onAutoCompleteSelectClearTextBox={false}
                                    style={{ marginRight: "10px" }}
                                    placeholder=""
                                    dataCy="schedule-serviceSelect"
                                />
                            )}
                            {this.state.SelectedService != null && (
                                <div className="open-schedule-divlabel">
                                    <div className="flex-container" style={{ justifyContent: "flex-start" }}>

                                        <div style={{ marginRight: "10px" }}>{this.state.SelectedService.GetNameWithDuration()} </div>
                                        <div
                                            className="open-schedule-redx pointer"
                                            onClick={() => {
                                                this.mutateState(_state => {
                                                    _state.SelectedService = null;
                                                });
                                            }}
                                        >
                                            <span>X</span>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="open-schedule-row">
                    <div className="open-schedule-c1">
                        <div className="open-schedule-divlabel"><label className="open-schedule-label">{cacheStore.GetString("DurationLabel") + " (min)"}</label></div>
                    </div>
                    <div className="open-schedule-c2">
                        <div className="open-schedule-divlabel">
                            <TextboxControl
                                IsNumeric={true}
                                value={this.state.ServiceDurationMinutes == null ? "" : this.state.ServiceDurationMinutes.toString()}
                                onChange={(value: number) => {
                                    this.mutateState(_state => {
                                        _state.ServiceDurationMinutes = Number(value);
                                    });
                                }}
                                style={{ marginRight: "10px", width: "60px" }}
                                placeholder=""
                                className="form-control"
                                valid={this.state.ServiceDurationMinutes > 0}
                                dataCy="schedule-duration"
                            />
                        </div>
                    </div>
                </div>

                {this.state.SelectedEmployee?.EmployeeModel.BookingHubEnabled && !!this.state.SelectedService && (
                    <div className="open-schedule-row">
                        <div className="open-schedule-c1">
                            <div className="open-schedule-divlabel"><label className="open-schedule-label">{cacheStore.GetString("BookingHubLabel")}</label></div>
                        </div>
                        <div className="open-schedule-c2">
                            <div className="open-schedule-divmessage">{this.getBookingHubPublishMessage(BookingHubVisibility.All)}</div>
                        </div>
                    </div>
                )}

                {this.state.SelectedService && cacheStore.ClinicSettings.EnableOnlinePredefinedAppointments && (
                    <EditServiceOptions
                        selectedService={this.state.SelectedService.ServiceDefinitionModel}
                        onIsOnlineChange={isOnline => this.mutateState(_state => _state.SelectedService.ServiceDefinitionModel.Model.IsAvailableOnline = isOnline)}
                        onTargetedClientChange={targetedClient => this.mutateState(_state => _state.SelectedService.ServiceDefinitionModel.Model.OnlineClienenteleCible = targetedClient)}
                    />
                )}
            </div>
        );
    }

    private renderLeftOpenRuleset(): JSX.Element {
        let editedRuleSet = this.props.Model.get("SelectedRuleset");
        const isComplex = SchedulerHelper.isServiceListComplex(this.state.ruleset_EditedRulesetDTO?.selectedComboServiceList);

        return (
            <div>
                <div className="open-schedule-row">
                    <div className="open-schedule-c1">
                        <div className="open-schedule-divlabel"><label className="open-schedule-label">{cacheStore.GetString("DateLabel")}</label></div>
                    </div>
                    <div className="open-schedule-c2">
                        <div className="open-schedule-divlabel">

                            <TextboxControlWithDatePicker
                                id="resolutionDP"
                                disabled={this.state.SelectedEmployee == null}
                                textValue={this.state.SelectedDaytext}
                                dateValue={this.state.SelectedDay}
                                width={150}
                                expectedPrecisionMinimum={LifeStageDatePrecisionEnum.Day}
                                onChange={(datePrecision: DateTimeWithPrecision) => {
                                    if (datePrecision.status == LifeStageDateStatusEnum.Valid)
                                        this.mutateState(
                                            _state => {
                                                _state.SelectedDaytext = datePrecision.text;
                                                _state.SelectedDay = datePrecision.value;
                                            },
                                            () => { this.throttledRefreshCalendar(); }
                                        );
                                }}
                                dataCy="schedule-date"
                            />
                        </div>
                    </div>
                </div>

                <div className="open-schedule-row">
                    <div className="open-schedule-c1">
                        <div className="open-schedule-divlabel"><label className="open-schedule-label">{cacheStore.GetString("FromLabel")}</label></div>
                    </div>
                    <div className="open-schedule-c2">
                        <div className="flex-container" style={{ justifyContent: "flex-start" }}>
                            <div style={{ width: "150px" }}>
                                <TimeSelector
                                    interval={5}
                                    id="new_sched_starttime"
                                    cssClass="rounded"
                                    date={this.state.HourStart}
                                    width={150}
                                    onChangeCallback={(_date: Date) => {
                                        this.mutateState(_state => {
                                            _state.HourStart = _date;
                                        });
                                    }}
                                    disabled={false}
                                    isRequired
                                    dataCy="schedule-from"
                                />
                            </div>
                            <div className="open-schedule-divlabel" style={{ width: "35px", paddingLeft: "10px" }}><label className="open-schedule-label">{cacheStore.GetString("ToLabel")}</label></div>
                            <div>
                                <TimeSelector
                                    interval={5}
                                    id="new_sched_endtime"
                                    cssClass="rounded"
                                    date={this.state.HourEnd}
                                    width={150}
                                    onChangeCallback={(_date: Date) => {
                                        this.mutateState(_state => {
                                            _state.HourEnd = _date;
                                        });
                                    }}
                                    disabled={false}
                                    isRequired
                                    dataCy="schedule-to"
                                />
                            </div>
                        </div>
                    </div>
                </div>
                <div className="open-schedule-row">
                    <div className="open-schedule-c1">
                        <div className="open-schedule-divlabel"><label className="open-schedule-label">{cacheStore.GetString("ServicesLabel")}</label></div>
                    </div>
                    <div className={`open-schedule-c2 ${styles.startOverButtonContainer}`}>
                        {/* Sizes are fixed in startOverButtonContainer css class. Need to be fixed when design system has the Button Label */}
                        <ButtonControl
                            variant="neutral"
                            disabled={this.state.SelectedEmployee == null}
                            onClick={() => this.handleStartOverClick()}
                            label={cacheStore.GetString("StartOverLabel")}
                        />
                        <ButtonControl
                            variant="brand"
                            disabled={this.state.SelectedEmployee == null}
                            onClick={() => this.showAddServicePopup()}
                            label={cacheStore.GetString("AddServicesLabel")}
                            dataCy="schedule-addServices"
                        />
                    </div>
                </div>

                {this.state.SelectedEmployee?.EmployeeModel.BookingHubEnabled && !!this.state.ruleset_EditedRulesetDTO?.selectedComboServiceList.count() && (
                    <div className="open-schedule-row">
                        <div className="open-schedule-c1">
                            <div className="open-schedule-divlabel"><label className="open-schedule-label">{cacheStore.GetString("BookingHubLabel")}</label></div>
                        </div>
                        <div className="open-schedule-c2">
                            <div className="open-schedule-divmessage">{this.getBookingHubPublishMessage(isComplex ? BookingHubVisibility.None : BookingHubVisibility.All)}</div>
                        </div>
                    </div>
                )}

                {this.state.SelectedEmployee != null && (
                    <RulesetEditComponent
                        selectedComboServiceList={this.state.SelectedComboServiceList}
                        employeeId={this.state.SelectedEmployee.ContactFacetId}
                        rulesetId={editedRuleSet?.Model.ID}
                        notifyParentCurrentStateChanged={editedRulesetDto => this.mutateState(s => s.ruleset_EditedRulesetDTO = editedRulesetDto)}
                    />
                )}
            </div>
        );
    }

    private renderLeftOpenTemplate(): JSX.Element {
        return (
            <div>
                <div className="open-schedule-row">
                    <div className="open-schedule-c1">
                        <div className="open-schedule-divlabel"><label className="open-schedule-label">{cacheStore.GetString("DateLabel")}</label></div>
                    </div>
                    <div className="open-schedule-c2">
                        <div className="open-schedule-divlabel">

                            <TextboxControlWithDatePicker
                                id="resolutionDP"
                                disabled={this.state.SelectedEmployee == null}
                                textValue={this.state.SelectedDaytext}
                                dateValue={this.state.SelectedDay}
                                width={150}
                                expectedPrecisionMinimum={LifeStageDatePrecisionEnum.Day}
                                onChange={(datePrecision: DateTimeWithPrecision) => {
                                    if (datePrecision.status == LifeStageDateStatusEnum.Valid)
                                        this.mutateState(
                                            _state => {
                                                _state.SelectedDaytext = datePrecision.text;
                                                _state.SelectedDay = datePrecision.value;
                                            },
                                            () => { this.throttledRefreshCalendar(); }
                                        );
                                }}
                                dataCy="schedule-date"
                            />
                        </div>
                    </div>
                </div>
                <div className="open-schedule-row">
                    <div className="open-schedule-c1">
                        <div className="open-schedule-divlabel"><label className="open-schedule-label">{cacheStore.GetString("GabaritLabel")}</label></div>
                    </div>
                    <div className="open-schedule-c2">
                        <div className="open-schedule-divlabel">
                            {this.state.SelectedEmployee != null && this.state.TemplateList != null && this.state.TemplateList.length >= 1 && (
                                <Combobox
                                    HasEmptyItem={true}
                                    doInitialChangeOp={false}
                                    id="_TemplateComboBox"
                                    items={this.getTemplateListItems().toImmutable()}
                                    selectedCode={this.state.SelectedTemplateId}
                                    onChange={selectedId => this.mutateState(a => a.SelectedTemplateId = selectedId, this.onTemplateReload)}
                                    dataCy="schedule-template"
                                />
                            )}
                            {this.state.SelectedEmployee == null &&
                                <span className="red">{cacheStore.GetString("ChooseEmployeeFirstLabel")}</span>
                            }
                        </div>
                    </div>
                </div>

                {this.state.SelectedEmployee?.EmployeeModel.BookingHubEnabled && !!this.state.SelectedTemplateId && (
                    <div className="open-schedule-row">
                        <div className="open-schedule-c1">
                            <div className="open-schedule-divlabel"><label className="open-schedule-label">{cacheStore.GetString("BookingHubLabel")}</label></div>
                        </div>
                        <div className="open-schedule-c2">
                            <div className="open-schedule-divmessage">{this.getBookingHubPublishMessage(this.state.templateBookingHubVisibility)}</div>
                        </div>
                    </div>
                )}

                <div>
                    <AppointmentSchedulerV2
                        fullWidth={this.state.templateHasRuleSets ? true : false}
                        workDays={this.state.TemplateWorkDays}
                        minorAxisResolutionInMinutes={15}
                        refresh={this.throttledRefreshCalendar.bind(this)}
                    />
                </div>
            </div>
        );
    }

    private renderRight(): JSX.Element {
        let selectedRulesetId = null;
        if (this.props.Model.get("SelectedRuleset") != null && this.props.Model.get("SelectedRuleset").Model != null)
            selectedRulesetId = this.props.Model.get("SelectedRuleset").Model.ID;

        return (
            <div className="open-schedule-rightside">

                <div className="flex-container">
                    <div className="flex-container" style={{ width: "100%", textAlign: "center", justifyContent: "space-evenly" }}>
                        <div>
                            <ButtonControl
                                variant="brand"
                                label="<"
                                onClick={() => this.mutateState(
                                    s => {
                                        s.SelectedDay = this.state.SelectedDay.addDays(-1);
                                        s.SelectedDaytext = s.SelectedDay.toMoment().format("YYYY-MM-DD");
                                    },
                                    () => this.throttledRefreshCalendar()
                                )}
                            />
                        </div>
                        <div style={{ padding: "10px" }}>
                            <span>{MyleHelper.getWeekdayName(this.state.SelectedDay.getDay())}, </span> <span>{moment(this.state.SelectedDay).format("DD MMMM YYYY")}</span>
                        </div>
                        <div>
                            <ButtonControl
                                variant="brand"
                                label=">"
                                onClick={() => this.mutateState(
                                    s => {
                                        s.SelectedDay = this.state.SelectedDay.addDays(1);
                                        s.SelectedDaytext = s.SelectedDay.toMoment().format("YYYY-MM-DD");
                                    },
                                    () => this.throttledRefreshCalendar()
                                )}
                            > &gt;
                            </ButtonControl>
                        </div>
                    </div>

                    <div style={{ paddingRight: "10px", paddingBottom: "10px", width: "145px" }}>
                        <Combobox
                            HasEmptyItem={false}
                            doInitialChangeOp={false}
                            id="_ResolutionComboBox"
                            items={[5, 10, 15, 20, 30, 60].map(x => new ComboBoxDropDownItem(x.toString(), x.toString() + " " + cacheStore.GetString("MinutesLabel"), 0)).toImmutable()}
                            selectedCode={this.state.PreferedDisplayResolution.toString()}
                            onChange={newResolution => this.mutateState(s => s.PreferedDisplayResolution = Number(newResolution))}
                        />
                    </div>

                    <div style={{ paddingRight: "10px", paddingBottom: "10px" }}>
                        <RefreshButton className="button-margin-right-refresh-cal" onClick={this.throttledRefreshCalendar.bind(this)} />
                    </div>

                </div>
                <div data-cy="schedule-container">
                    <AppointmentSchedulerV2
                        workDays={this.state.WorkDays}
                        minorAxisResolutionInMinutes={this.state.PreferedDisplayResolution}
                        onSelectRulesetOperation={LocalOperations.OpenScheduleTab.SelectExistingRuleset}
                        selectedRulesetId={selectedRulesetId}
                        fullWidth={false}
                        deleteSinglePredefinedAppointmentOrRuleSetFunction={this.deleteSinglePredefinedAppointmentOrRuleSet.bind(this)}
                        showXForDelete={true}
                        refresh={this.throttledRefreshCalendar.bind(this)}
                    />

                </div>
            </div>
        );
    }

    private renderHeaderRow(): JSX.Element {
        return (
            <div className="myle-workspace-header" style={{ padding: "0px" }}>
                <div className="row">
                    <div className="col-lg-4 col-md-5 col-sm-5 hidden-sm-down">
                        <h3 className="thin-font" style={{ margin: "9px" }}>{cacheStore.GetString("ScheduleAssignmentLabel")}</h3>
                    </div>
                </div>
            </div>
        );
    }

    private mutateState(Mutation: (s: OpenScheduleTabViewState) => void, callback: () => any = null): void {
        let state = new OpenScheduleTabViewState();
        state = this.state;
        Mutation(state);
        this.setState(state, callback);
    }

    public clearRulesetSelection(): void {
        this.mutateState(
            s => {
                s.ruleset_EditedRulesetDTO = null;
            },
            () => sessionStore.dispatch(LocalOperations.OpenScheduleTab.ClearRulesetInfo)
        );
    }

    private onAddAffectation(): void {
        const minuteInTicks = 600000000;

        if (this.state.OpenSheduleType === OpenSheduleType.RuleSet) {
            this.SaveNewRuleSet();
        }
        else if (this.state.OpenSheduleType === OpenSheduleType.Service) {
            let startDate = new Date(this.state.SelectedDay.getTime());
            startDate.setHours(this.state.HourStart.getHours());
            startDate.setMinutes(this.state.HourStart.getMinutes(), 0);

            let endDate = new Date(this.state.SelectedDay.getTime());
            endDate.setHours(this.state.HourEnd.getHours());
            endDate.setMinutes(this.state.HourEnd.getMinutes(), 0);

            let requestData = {
                ServiceDefinitionId: this.state.SelectedService.ServiceDefinitionModel.Model.Id,
                IsOnlineBookingAllowed: this.state.SelectedService.ServiceDefinitionModel.Model.IsAvailableOnline,
                TargetedClient: this.state.SelectedService.ServiceDefinitionModel.Model.OnlineClienenteleCible,
                Duration: this.state.ServiceDurationMinutes * minuteInTicks,
                ServiceName: this.state.SelectedService.GetName(),
                EmployeeId: this.state.SelectedEmployee.ContactFacetId,
                StartDate: MyleHelper.GetStandardizedDateString(startDate),
                EndDate: MyleHelper.GetStandardizedDateString(endDate),
                RecurrenceType: this.state.selectedRecurrenceType,
                RecurrenceEndDate: this.state.RecurrenceEndDate,
                SiteId: this.state.SelectedSiteId,
                IsBookingHubAvailability: true
            };

            Client.Postv2(
                ServerOperations.Server.Myle_Scheduling_AddServicePredefinedAppointment,
                requestData,
                () => {
                    this.mutateState((s: OpenScheduleTabViewState) => {
                        s.AddPopupOpen = false;
                        s.waitWhileAddingPopup = false;
                    }, () => sessionStore.hideGlobalOverlay());

                    this.throttledRefreshCalendar();
                },
                error => {
                    this.mutateState((s: OpenScheduleTabViewState) => {
                        s.waitWhileAddingPopup = false;
                    });
                    sessionStore.showPopup(error);
                }
            );

            this.mutateState((s: OpenScheduleTabViewState) => {
                s.AddPopupOpen = false;
                s.waitWhileAddingPopup = true;
                s.hasDoubleBookingCollision = false;
            });
        }
    }

    public SaveNewRuleSet(): void {
        let startDate = new Date(this.state.SelectedDay.getTime());
        startDate.setHours(this.state.HourStart.getHours());
        startDate.setMinutes(this.state.HourStart.getMinutes(), 0);

        let endDate = new Date(this.state.SelectedDay.getTime());
        endDate.setHours(this.state.HourEnd.getHours());
        endDate.setMinutes(this.state.HourEnd.getMinutes(), 0);

        let editedRuleSetData = this.state.ruleset_EditedRulesetDTO;

        let requestData = {
            SelectedRuleSet: editedRuleSetData.selectedComboServiceList,
            EmployeeId: this.state.SelectedEmployee.ContactFacetId,
            CreatedByEmployeeId: sessionStore.getCurrentEmployee().ContactFacetId,
            StartDate: MyleHelper.GetTimezoneAgnosticDatetimeFormat(startDate),
            EndDate: MyleHelper.GetTimezoneAgnosticDatetimeFormat(endDate),
            RecurrenceType: this.state.selectedRecurrenceType,
            RecurrenceEndDate: this.state.RecurrenceEndDate,
            SiteId: this.state.SelectedSiteId,
            IsBookingHubAvailability: !SchedulerHelper.isServiceListComplex(editedRuleSetData.selectedComboServiceList)
        };

        Client.Postv2(
            ServerOperations.Server.Myle_Scheduling_AddRulesSet,
            requestData,
            () => {
                this.mutateState(
                    s => {
                        s.waitWhileAddingPopup = false;
                        s.hasDoubleBookingCollision = false;
                    },
                    () => sessionStore.hideGlobalOverlay()
                );

                this.throttledRefreshCalendar();
            },
            error => sessionStore.showPopup(error)
        );

        // Is Loading
        this.mutateState(s => {
            s.AddPopupOpen = false;
            s.waitWhileAddingPopup = true;
            s.hasDoubleBookingCollision = false;
        });
    }

    private throttledRefreshCalendar(): void {
        // Throlle
        if (this.timeoutReferenceRefreshCalendar !== -1) {
            clearTimeout(this.timeoutReferenceRefreshCalendar);
            this.timeoutReferenceRefreshCalendar = -1;
        }
        this.timeoutReferenceRefreshCalendar = window.setTimeout(() => {
            this.refreshCalendar();
        }, 250);
    }

    // Use the Throtled One http://imgur.com/gallery/xn4cYl4
    private refreshCalendar(): void {
        if (this.state.SelectedEmployee == null)
            return;

        this.LastCalendarRequest = GuidHelper.NewGuid();
        let requestData = {
            Start: MyleHelper.GetStandardizedDateString(this.state.SelectedDay.toMoment().startOf("day").toDate()),
            End: MyleHelper.GetStandardizedDateString(this.state.SelectedDay.toMoment().endOf("day").toDate()),

            SearchText: "",

            IncludeContactPoint: false,
            IncludeCancelled: false,
            SitesId: cacheStore.SiteCache.map(x => x.Model.Id),
            RelatedEmployees: [this.state.SelectedEmployee.ContactFacetId],
            IncludePredefinedAppointments: false,
            RequestReferenceId: this.LastCalendarRequest
        };

        Client.Postv2(
            ServerOperations.Server.Myle_SearchAppointments,
            requestData,
            response => {
                if (this.LastCalendarRequest != response.RequestReferenceId)
                    return;

                this.mutateState(s => {
                    s.IsLoading = false;

                    let RequestedStart: Date = moment(response.RequestedStart).toDate();

                    let RequestedEmployees: string[] = response.RequestedEmployees;

                    let appts = Immutable.List<Appointment>(response.Appointments);
                    let preds = Immutable.List<PredefinedAppointment>(response.PredefinedAppointments);

                    let ruleSet = Immutable.List<PredefinedRuleSet>(response.PredefinedScheduleRuleSet);

                    let WorkDays = [];

                    RequestedEmployees.forEach(employee => {
                        let workDay = new WorkDayV2();
                        workDay.date = moment(RequestedStart).startOf("day").toDate();
                        workDay.EmployeeId = employee;

                        workDay.calendarSlots = [];

                        let _appts = appts.toArray().filter(x => x.EmployeInactivableId == employee);
                        _appts.forEach(x => workDay.calendarSlots.push(CalendarSlotV2.FromAppoinment(x)));

                        let _preds = preds.filter(x => x.EmployeeId == employee);
                        _preds.forEach(x => workDay.calendarSlots.push(CalendarSlotV2.FromPredefinedAppoinment(x)));

                        let _ruleSet = ruleSet.toArray().filter(x => x.EmployeeId == employee);
                        _ruleSet.forEach(x => {
                            workDay.calendarSlots.push(CalendarSlotV2.FromPredefinedRuleSet(x));

                            // We do not need the Debug line for Slots anymore
                            // let RSslots = Immutable.List<PredefinedScheduleRuleSetSlotDTO>(x.PredefinedRuleSetSlots);
                            // RSslots.forEach(z => workDay.calendarSlots.push(CalendarSlotV2.FromPredefinedRuleSetSlot(z)));
                        });

                        workDay.HasRuleSet = _ruleSet.length > 0;
                        workDay.calendarSlots = workDay.calendarSlots.sortByProperty(slot => slot.fromRaw);

                        if (this.state.SelectedEmployee != null && this.state.SelectedEmployee.ContactFacetId == RequestedEmployees[0]) {
                            WorkDays.push(workDay);
                        }

                        s.WorkDays = WorkDays.toImmutable();
                    });
                });
            },
            () => this.mutateState(s => s.IsLoading = false)
        );

        this.mutateState((s: OpenScheduleTabViewState) => {
            s.IsLoading = true;
            s.WorkDays = [].toImmutable();
        });
    }

    private onShowDeletePopup(): void {
        this.mutateState(s => {
            s.DeletePopupOpen = true;
            s.RecurrenceEndDateDelete = s.SelectedDay;
            s.HourStartDelete = s.HourStart;
            s.HourEndDelete = s.HourEnd;
        });

        sessionStore.showGlobalOverlay(true);
    }

    private onContinueDeleteClick(): void {
        let startDate = new Date(this.state.SelectedDay.getTime());
        startDate.setHours(this.state.HourStartDelete.getHours(), this.state.HourStartDelete.getMinutes(), 0);

        let endDate = new Date(this.state.SelectedDay.getTime());
        endDate.setHours(this.state.HourEndDelete.getHours(), this.state.HourEndDelete.getMinutes(), 0);

        let requestData = {
            EmployeeId: this.state.SelectedEmployee.ContactFacetId,
            StartDate: MyleHelper.GetStandardizedDateString(startDate),
            EndDate: MyleHelper.GetStandardizedDateString(endDate),
            RecurrenceType: this.state.selectedRecurrenceTypeDelete,
            RecurrenceEndDate: this.state.RecurrenceEndDateDelete,
            SitesId: this.state.SitesListForDelete.toArray().filter(c => c.Checked === true).map(c => c.Model.Id),
            DeleteType: this.state.selectedDeleteType
        };

        this.mutateState((s: OpenScheduleTabViewState) => {
            s.DeletePopupOpen = false;
            s.waitWhileAddingPopup = true;

            Client.Postv2(
                ServerOperations.Server.Myle_Scheduling_DeleteServiceAndRuleSet,
                requestData,
                () => {
                    this.mutateState(s => s.waitWhileAddingPopup = false, () => this.throttledRefreshCalendar());
                    sessionStore.hideGlobalOverlay();
                },
                error => sessionStore.showPopup(error)
            );
        });
    }

    private checkRuleSetAvailability(): void {
        if (this.state.OpenSheduleType === OpenSheduleType.RuleSet) {
            let startDate = new Date(this.state.SelectedDay.getTime());
            startDate.setHours(this.state.HourStart.getHours());
            startDate.setMinutes(this.state.HourStart.getMinutes(), 0);

            let endDate = new Date(this.state.SelectedDay.getTime());
            endDate.setHours(this.state.HourEnd.getHours());
            endDate.setMinutes(this.state.HourEnd.getMinutes(), 0);

            let requestData = {
                EmployeeId: this.state.SelectedEmployee.ContactFacetId,
                StartDate: MyleHelper.GetStandardizedDateString(startDate),
                EndDate: MyleHelper.GetStandardizedDateString(endDate),
                RecurrenceType: this.state.selectedRecurrenceType,
                RecurrenceEndDate: this.state.RecurrenceEndDate
            };

            Client.Post(
                ServerOperations.Server.Myle_Scheduling_CheckRuleSetAvailability,
                requestData,
                response => {
                    let dateList: Date[] = response.ReservedAvailabilty;
                    if (!dateList) {
                        sessionStore.hideGlobalOverlay();
                        return;
                    }

                    this.mutateState(s => {
                        s.AddPopupOpen = false;

                        if (dateList.length > 0) {
                            s.hasDoubleBookingCollision = true;
                            s.ReservedDate = dateList;
                            return;
                        }

                        this.onAddAffectation();
                        sessionStore.hideGlobalOverlay();
                    });
                }
            );
        }
    }

    private onTypeChange(typeCode: string): void {
        const openSheduleType = EnumHelper.getValue(OpenSheduleType, typeCode);

        this.mutateState(
            s => s.OpenSheduleType = openSheduleType,
            () => {
                if (openSheduleType === OpenSheduleType.Template && this.state.SelectedEmployee != null) {
                    this.loadEmployeeTemplate();
                    return;
                }
            }
        );
    }

    private handleStartOverClick(): void {
        this.mutateState(
            s => {
                s.ruleset_EditedRulesetDTO = null;
                s.SelectedComboServiceList = [].toImmutable();
                s.AddServicePopupOpen = true;
            },
            () => sessionStore.dispatch(LocalOperations.OpenScheduleTab.ClearRulesetInfo)
        );

        sessionStore.showGlobalOverlay(true);
    }

    private showAddServicePopup(isShow: boolean = true): void {
        this.mutateState(s => s.AddServicePopupOpen = isShow);
        if (isShow)
            sessionStore.showGlobalOverlay(true);
        else
            sessionStore.hideGlobalOverlay();
    }

    private onAddSelectedComboService(): void {
        this.mutateState(
            s => {
                s.AddServicePopupOpen = false;
                s.SelectedComboServiceList = this.props.Model.get("SelectedComboServiceList");
            },
            () => {
                sessionStore.hideGlobalOverlay();
                sessionStore.EmitChangeToListeners();
            }
        );
    }

    public clearRuleSetEdit(s: OpenScheduleTabViewState): OpenScheduleTabViewState {
        s.ruleset_EditedRulesetDTO = null;
        return s;
    }

    private openAddSchedulePopup(isRuleSet: boolean = false): void {
        if (isRuleSet) { // MinCheck
            let startDate = new Date(this.state.SelectedDay.getTime());
            startDate.setHours(this.state.HourStart.getHours());
            startDate.setMinutes(this.state.HourStart.getMinutes(), 0);

            let endDate = new Date(this.state.SelectedDay.getTime());
            endDate.setHours(this.state.HourEnd.getHours());
            endDate.setMinutes(this.state.HourEnd.getMinutes(), 0);

            let minMaxResults = SchedulerHelper.calculateTotalMinAndMax(this.state.ruleset_EditedRulesetDTO.selectedComboServiceList.toArray(), startDate, endDate);
            if (!minMaxResults.MinimumsValid) {
                this.mutateState(s => s.MinErrorPopupOpen = true);
                return;
            }
        }

        // Open Add Schedule Popup
        this.mutateState(s => {
            s.AddPopupOpen = true;
            if (!s.RecurrenceEndDate)
                s.RecurrenceEndDate = s.SelectedDay;
        });
        sessionStore.showGlobalOverlay(true);
    }

    private onSelectAllSiteForDeleteClick(): void {
        this.mutateState(s => {
            s.SelectAllSiteForDelete = !s.SelectAllSiteForDelete;

            s.SitesListForDelete.toArray().map(c => {
                c.Checked = s.SelectAllSiteForDelete;

                return c;
            }).toImmutable();
        });
    }

    private loadEmployeeTemplate(): void {
        let requestData = {
            ServiceDefinitionIds: this.state.SelectedEmployee.EmployeeModel.ServicesOffered.map(c => c.ServiceDefinitionModel.Model.Id)
        };

        if (requestData.ServiceDefinitionIds && requestData.ServiceDefinitionIds.length > 0) {
            Client.Post(
                ServerOperations.Server.Myle_Scheduling_LoadLightTemplateForSpecificEmployee,
                requestData,
                response => {
                    let templateList: TemplateLight[] = response.TemplateList;

                    this.mutateState(s => {
                        if (templateList != null && templateList.length > 0)
                            s.TemplateList = templateList;
                        else
                            s.TemplateList = [];
                    });
                }
            );
        }
        else {
            this.mutateState(s => s.TemplateList = []);
        }
    }

    private onTemplateReload(): void {
        let requestData = {
            Id: this.state.SelectedTemplateId
        };

        Client.Post(
            ServerOperations.Server.Myle_Scheduling_GetTemplateInfo,
            requestData,
            response => {
                this.mutateState((s: OpenScheduleTabViewState) => {
                    let templateInfo: TemplateInfo[] = response.TemplateInfo;
                    let ruleSet = Immutable.List<PredefinedRuleSet>(response.RulesetInfo);

                    let workDays = [];

                    let workDay = new WorkDayV2();
                    workDay.date = moment().startOf("day").toDate();
                    workDay.calendarSlots = [];
                    workDay.HasRuleSet = false;

                    let bookingHubVisibility = BookingHubVisibility.None;
                    if (templateInfo != null && templateInfo.length > 0) {
                        templateInfo.forEach((item: TemplateInfo) => {
                            if (sessionStore.userLanguageCode === "fr")
                                item.Name = item.NameFR;
                            else if (sessionStore.userLanguageCode === "en")
                                item.Name = item.NameEN;
                            else if (sessionStore.userLanguageCode === "es-MX")
                                item.Name = item.NameEN;

                            workDay.calendarSlots.push(CalendarSlotV2.fromTemplateInfo(item));
                        });

                        let serviceDefinitionList = templateInfo.map(n => n.ServiceDefinitionId);
                        let unique = serviceDefinitionList.filter((v, i, a) => a.indexOf(v) === i);
                        let serviceDef = cacheStore.ServiceDefinitionCache.filter(s => unique.some(u => u == s.Model.Id));

                        let employeeList: ServiceDefinitionModelEmployee[] = [];

                        serviceDef.forEach(sd => {
                            if (employeeList.length === 0)
                                employeeList = sd.Employees.map(x => x.clone());
                            else
                                employeeList = employeeList.filter(c => sd.Employees.some(u => u.ContactFacetId === c.ContactFacetId));
                        });

                        s.templateHasRuleSets = false;
                        bookingHubVisibility = BookingHubVisibility.All;
                    }
                    if (ruleSet != null && ruleSet.size > 0) {
                        let hasRuleSet = false;
                        let hasSimpleRuleSet = false;
                        let hasComplexRuleSet = false;

                        ruleSet.forEach(x => {
                            x.StartDate = moment(x.StartDate)
                                .setToday()
                                .toISOString();

                            x.EndDate = moment(x.EndDate)
                                .setToday()
                                .toISOString();

                            workDay.calendarSlots.push(CalendarSlotV2.FromPredefinedRuleSet(x));

                            hasRuleSet = true;

                            const isComplex = SchedulerHelper.isPredefinedRuleSetComplex(x);

                            hasSimpleRuleSet = hasSimpleRuleSet || !isComplex;
                            hasComplexRuleSet = hasComplexRuleSet || isComplex;
                        });

                        workDay.HasRuleSet = hasRuleSet;
                        s.templateHasRuleSets = true;

                        if (!hasComplexRuleSet && hasSimpleRuleSet) {
                            bookingHubVisibility = BookingHubVisibility.All;
                        }
                        else if (hasComplexRuleSet && (hasSimpleRuleSet || bookingHubVisibility === BookingHubVisibility.All)) {
                            bookingHubVisibility = BookingHubVisibility.Partial;
                        }
                    }

                    workDay.calendarSlots = workDay.calendarSlots.sortByProperty(n => n.fromRaw);
                    workDays.push(workDay);

                    s.TemplateWorkDays = workDays.toImmutable();
                    s.templateBookingHubVisibility = bookingHubVisibility;
                });
            },
            () => {

            }
        );
    }

    private onAddTemplateClick(): void {
        let startDate = new Date(this.state.SelectedDay.getTime());

        let requestData = {
            EmployeeId: this.state.SelectedEmployee.ContactFacetId,
            CreatedByEmployeeId: sessionStore.getCurrentEmployee().ContactFacetId,
            StartDate: MyleHelper.GetTimezoneAgnosticDatetimeFormat(startDate),
            RecurrenceType: this.state.selectedRecurrenceType,
            RecurrenceEndDate: this.state.RecurrenceEndDate,
            SelectedTemplateId: this.state.SelectedTemplateId,
            IsRuleSetTemplate: this.state.templateHasRuleSets,
            IsBookingHubAvailability: this.state.templateBookingHubVisibility !== BookingHubVisibility.None
        };

        this.mutateState((s: OpenScheduleTabViewState) => {
            s.AddPopupOpen = false;
            s.waitWhileAddingPopup = true;
        });

        Client.Post(
            ServerOperations.Server.Myle_Scheduling_AddScheduleFromTemplate,
            requestData,
            () => {
                this.mutateState((s: OpenScheduleTabViewState) => {
                    s.waitWhileAddingPopup = false;
                });

                sessionStore.hideGlobalOverlay();
                this.throttledRefreshCalendar();
            }
        );
    }

    private deleteSinglePredefinedAppointmentOrRuleSet(slot: CalendarSlotV2): void {
        let requestData = {
            PredefinedAppointmentId: "",
            RuleSetId: "",
            Type: 0
        };

        if (slot.Type === CalendarSlotType.RuleSet)
            requestData.RuleSetId = slot.Model.ID;
        else if (slot.Type === CalendarSlotType.PredefinedAppointment)
            requestData.PredefinedAppointmentId = slot.Model.Id;

        requestData.Type = slot.Type;

        Client.Post(
            ServerOperations.Server.Myle_Scheduling_DeleteSinglePredefinedAppointmentOrRuleSet,
            requestData,
            () => {
                this.throttledRefreshCalendar();
            }
        );
    }

    private getBookingHubPublishMessage(bookingHubVisibility: BookingHubVisibility): string {
        let vocabKey: string;

        switch (bookingHubVisibility) {
            case BookingHubVisibility.All:
                vocabKey = "BookingHub_ScheduleWillBePublishedMessage";
                break;

            case BookingHubVisibility.None:
                vocabKey = "BookingHub_ScheduleWillNotBePublishedMessage";
                break;

            case BookingHubVisibility.Partial:
                vocabKey = "BookingHub_ScheduleWillBePartiallyPublishedMessage";
                break;
        }

        return cacheStore.GetString(vocabKey);
    }

    private getTemplateListItems(): ComboBoxDropDownItem[] {
        return this.state.TemplateList.map(x => TemplateLight.toComboboxItemWithSiteId(x, this.getSiteName(x.SiteId))).sortByProperty(cb => cb.DisplayName);
    }

    private getSiteName(id: string): string {
        const sites = cacheStore.SiteCache;
        return sites.firstOrDefault(s => s.getId() == id).Model.ClinicalName;
    }
}

export { OpenScheduleTabView, OpenScheduleTabViewState, OpenSheduleType };
